import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Calendar, Plus, Loader2, Plane, Clock, Repeat, ArrowRight, Trash2, Pencil, Rows3, LayoutGrid, Pause, Play, Copy, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { GenerateFlightsDialog } from '../components/GenerateFlightsDialog';
import { UnconfirmedCrewCard } from '../components/crew/UnconfirmedCrewCard';
import { FICHA_LABEL } from '../components/crew/crewPositions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayLabels = { 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 7: 'Dom' };

// Auditoria 6 - Fase E Item 18: Vista Gantt de rutas
function GanttView({ token }) {
    const today = new Date();
    const in13d = new Date(today.getTime() + 13 * 86400000);
    const [range, setRange] = useState({
        date_from: today.toISOString().slice(0, 10),
        date_to: in13d.toISOString().slice(0, 10),
    });
    const [routes, setRoutes] = useState([]);
    const [dates, setDates] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchGantt = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/schedules/gantt`, {
                params: range,
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoutes(res.data.routes || []);
            // Compute date columns
            const start = new Date(range.date_from);
            const end = new Date(range.date_to);
            const cols = [];
            const cur = new Date(start);
            while (cur <= end) {
                cols.push({ date: cur.toISOString().slice(0, 10), dow: ((cur.getDay() + 6) % 7) + 1 });
                cur.setDate(cur.getDate() + 1);
            }
            setDates(cols);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error cargando Gantt');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchGantt(); }, []);

    const cellForRoute = (route, d) => {
        const occ = route.occurrences.find(o => o.date === d.date);
        if (!occ) return <div className="h-8 border-r border-border/20" />;
        const flightGenerated = !!occ.flight_id;
        const bg = flightGenerated ? 'bg-primary/40' : 'bg-primary/15';
        const border = flightGenerated ? 'border-primary' : 'border-primary/30';
        return (
            <div className={`h-8 border-r border-border/20 flex items-center justify-center text-[10px] font-mono ${bg} border-l-2 ${border}`}
                 title={`${route.flight_number} · ${occ.dep_time} → ${occ.arr_time}${occ.arrives_next_day ? ' (+1)' : ''}${occ.aircraft_registration ? ' · ' + occ.aircraft_registration : ''}`}
                 data-testid={`gantt-cell-${route.flight_number}-${d.date}`}>
                {occ.dep_time.slice(0, 5)}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1">
                    <Label className="text-xs">Desde</Label>
                    <Input type="date" value={range.date_from} onChange={e => setRange({...range, date_from: e.target.value})}
                           className="input-aviation font-mono w-40" data-testid="gantt-from" />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Hasta</Label>
                    <Input type="date" value={range.date_to} onChange={e => setRange({...range, date_to: e.target.value})}
                           className="input-aviation font-mono w-40" data-testid="gantt-to" />
                </div>
                <Button onClick={fetchGantt} disabled={loading} className="btn-primary" data-testid="gantt-refresh">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                    Recargar
                </Button>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground ml-auto">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-primary/15 border-l-2 border-primary/30" />Programada</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-primary/40 border-l-2 border-primary" />Vuelo generado</span>
                </div>
            </div>
            {routes.length === 0 && !loading ? (
                <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Sin rutas activas en el rango</p>
                </CardContent></Card>
            ) : (
                <div className="border border-border/30 rounded-lg overflow-x-auto" data-testid="gantt-grid">
                    {/* Header dates */}
                    <div className="grid" style={{ gridTemplateColumns: `220px repeat(${dates.length}, minmax(48px, 1fr))` }}>
                        <div className="p-2 text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/30 border-b border-r border-border/30">
                            Ruta
                        </div>
                        {dates.map(d => {
                            const isWeekend = d.dow >= 6;
                            return (
                                <div key={d.date} className={`p-1 text-center border-b border-r border-border/20 ${isWeekend ? 'bg-muted/40' : 'bg-muted/20'}`}>
                                    <div className="text-[9px] text-muted-foreground">{dayLabels[d.dow]}</div>
                                    <div className="text-[10px] font-mono">{d.date.slice(5)}</div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Rows */}
                    {routes.map(route => (
                        <div key={route.id} className={`grid ${route.paused ? 'opacity-50' : ''}`} style={{ gridTemplateColumns: `220px repeat(${dates.length}, minmax(48px, 1fr))` }}
                             data-testid={`gantt-route-${route.flight_number}`}>
                            <div className="p-2 border-b border-r border-border/20 bg-muted/10 flex flex-col justify-center">
                                <div className="flex items-center gap-1.5 text-xs">
                                    <span className="font-mono font-bold text-primary">{route.flight_number}</span>
                                    <span className="text-muted-foreground text-[10px]">{route.origin}→{route.destination}</span>
                                    {route.paused && (
                                        <span className="text-[9px] font-bold text-amber-400 uppercase tracking-widest">Pausada</span>
                                    )}
                                </div>
                                <div className="text-[9px] text-muted-foreground font-mono">
                                    {route.departure_time}-{route.arrival_time}{route.arrives_next_day ? ' (+1)' : ''}
                                </div>
                            </div>
                            {dates.map(d => (
                                <div key={d.date} className="border-b border-border/20">
                                    {cellForRoute(route, d)}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SchedulePage() {
    const { token, hasRole } = useAuth();
    const [schedules, setSchedules] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [showGenerate, setShowGenerate] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [form, setForm] = useState({
        flight_number: '', origin: '', destination: '',
        departure_time: '06:00', arrival_time: '07:30',
        frequency: [1, 2, 3, 4, 5, 6, 7],
        effective_from: '', effective_to: '', default_aircraft_type: '',
        arrives_next_day: false,
    });
    const [editingSchedule, setEditingSchedule] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    // Auditoria 6 - Fase E Item 21: bulk crew assignment
    const [bulkDialog, setBulkDialog] = useState(null); // schedule
    const [pendingKey, setPendingKey] = useState(0);
    const [bulkForm, setBulkForm] = useState({
        date_from: '', date_to: '',
        assignments: [], overwrite: false, force: false,
    });
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [crewList, setCrewList] = useState([]);

    useEffect(() => {
        fetchSchedules();
        fetchAircraft();
    }, []);

    const fetchSchedules = async () => {
        try {
            const res = await axios.get(`${API}/schedules`, { headers });
            setSchedules(res.data);
        } catch (e) { toast.error('Error al cargar programaciones'); }
        finally { setLoading(false); }
    };

    const fetchAircraft = async () => {
        try {
            const res = await axios.get(`${API}/aircraft`, { headers });
            setAircraft(res.data);
        } catch (e) { /* silent */ }
    };

    const toggleDay = (day) => {
        setForm(prev => ({
            ...prev,
            frequency: prev.frequency.includes(day)
                ? prev.frequency.filter(d => d !== day)
                : [...prev.frequency, day].sort()
        }));
    };

    const handleCreate = async (confirmLong = false) => {
        if (!form.flight_number || !form.origin || !form.destination) {
            toast.error('Completa los campos obligatorios');
            return;
        }
        setSubmitting(true);
        const payload = confirmLong ? { ...form, confirm_long_duration: true } : form;
        try {
            if (editingSchedule) {
                await axios.put(`${API}/schedules/${editingSchedule.id}`, payload, { headers });
                toast.success('Programación actualizada');
            } else {
                await axios.post(`${API}/schedules`, payload, { headers });
                toast.success('Programación creada');
            }
            setShowCreate(false);
            setEditingSchedule(null);
            setForm({ flight_number: '', origin: '', destination: '', departure_time: '06:00', arrival_time: '07:30', frequency: [1,2,3,4,5,6,7], effective_from: '', effective_to: '', default_aircraft_type: '', arrives_next_day: false });
            fetchSchedules();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (e.response?.status === 409 && d?.type === 'unusual_duration') {
                setSubmitting(false);
                if (window.confirm(d.message)) return handleCreate(true);
                toast.info('Revisa los horarios de la ruta');
                return;
            }
            toast.error(typeof d === 'string' ? d : 'Error');
        }
        finally { setSubmitting(false); }
    };

    const openEditSchedule = (s) => {
        setEditingSchedule(s);
        setForm({
            flight_number: s.flight_number || '', origin: s.origin || '', destination: s.destination || '',
            departure_time: s.departure_time || '06:00', arrival_time: s.arrival_time || '07:30',
            frequency: s.frequency || [1,2,3,4,5,6,7],
            effective_from: s.effective_from || '', effective_to: s.effective_to || '',
            default_aircraft_type: s.default_aircraft_type || '',
            arrives_next_day: !!s.arrives_next_day,
        });
        setShowCreate(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/schedules/${id}`, { headers });
            toast.success('Programación desactivada');
            fetchSchedules();
        } catch (e) { toast.error('Error'); }
    };

    // Auditoria 6 - Fase E Item 19: pausar/reanudar ruta
    const handleTogglePause = async (s) => {
        try {
            const res = await axios.post(`${API}/schedules/${s.id}/toggle-pause`, {}, { headers });
            toast.success(res.data.paused ? `Ruta ${s.flight_number} pausada` : `Ruta ${s.flight_number} reanudada`);
            fetchSchedules();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    // Auditoria 6 - Fase E Item 20: duplicar ruta como plantilla
    const [duplicateDialog, setDuplicateDialog] = useState(null);
    const [duplicateFN, setDuplicateFN] = useState('');
    const openDuplicate = (s) => { setDuplicateDialog(s); setDuplicateFN(''); };
    const handleDuplicate = async () => {
        if (!duplicateDialog) return;
        const fn = duplicateFN.trim().toUpperCase();
        if (!fn) { toast.error('flight_number requerido'); return; }
        try {
            await axios.post(`${API}/schedules/${duplicateDialog.id}/duplicate`, { flight_number: fn }, { headers });
            toast.success(`Ruta ${duplicateDialog.flight_number} duplicada como ${fn}`);
            setDuplicateDialog(null);
            setDuplicateFN('');
            fetchSchedules();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al duplicar'); }
    };

    // Auditoria 6 - Fase E Item 21: bulk crew assignment
    const openBulkDialog = async (s) => {
        const today = new Date();
        const in30d = new Date(today.getTime() + 30 * 86400000);
        const dateFrom = today.toISOString().slice(0, 10);
        const dateTo = in30d.toISOString().slice(0, 10);
        setBulkDialog(s);
        setBulkForm({ date_from: dateFrom, date_to: dateTo, assignments: [], overwrite: false, force: false });
        setBulkPreview(null);
        try {
            const [pv, crewRes] = await Promise.all([
                axios.get(`${API}/schedules/${s.id}/future-flights`, { params: { date_from: dateFrom, date_to: dateTo }, headers }),
                axios.get(`${API}/crew`, { headers }),
            ]);
            setBulkPreview(pv.data);
            setCrewList(crewRes.data || []);
        } catch (e) { toast.error('Error cargando datos de la ruta'); }
    };

    const refreshBulkPreview = async () => {
        if (!bulkDialog) return;
        try {
            const pv = await axios.get(`${API}/schedules/${bulkDialog.id}/future-flights`, {
                params: { date_from: bulkForm.date_from, date_to: bulkForm.date_to }, headers,
            });
            setBulkPreview(pv.data);
        } catch (e) { toast.error('Error actualizando preview'); }
    };

    const addBulkAssignment = () => {
        setBulkForm(prev => ({ ...prev, assignments: [...prev.assignments, { crew_id: '', role: 'operating' }] }));
    };
    const updateBulkAssignment = (idx, patch) => {
        setBulkForm(prev => ({
            ...prev,
            assignments: prev.assignments.map((a, i) => i === idx ? { ...a, ...patch } : a),
        }));
    };
    const removeBulkAssignment = (idx) => {
        setBulkForm(prev => ({
            ...prev,
            assignments: prev.assignments.filter((_, i) => i !== idx),
        }));
    };

    const submitBulkAssign = async () => {
        if (!bulkDialog) return;
        const validAssigns = bulkForm.assignments.filter(a => a.crew_id);
        if (validAssigns.length === 0) { toast.error('Agrega al menos un tripulante'); return; }
        setBulkSubmitting(true);
        try {
            const res = await axios.post(`${API}/schedules/${bulkDialog.id}/assign-crew-bulk`, {
                assignments: validAssigns,
                date_from: bulkForm.date_from, date_to: bulkForm.date_to,
                overwrite: bulkForm.overwrite, force: bulkForm.force,
            }, { headers });
            const t = res.data.totals;
            let msg = `${t.assigned}/${t.targeted} asignados`;
            if (t.skipped_existing > 0) msg += ` · ${t.skipped_existing} con crew existente`;
            if (t.conflicts > 0) msg += ` · ${t.conflicts} conflictos`;
            if (t.missing_ratings > 0) msg += ` · ${t.missing_ratings} sin type rating`;
            if (t.assigned > 0) toast.success(msg);
            else toast.warning(msg);
            if (t.assigned > 0) toast.info('Las asignaciones quedan pendientes hasta que CCO o un administrador las confirme', { duration: 8000 });
            setPendingKey(k => k + 1);
            (res.data.position_errors || []).forEach(pe => toast.error(`${pe.flight_number} ${pe.date}: ${pe.message}`, { duration: 10000 }));
            setBulkDialog(null);
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al asignar en bloque'); }
        finally { setBulkSubmitting(false); }
    };

    const canEdit = hasRole && (hasRole('admin') || hasRole('comercial'));
    const canBulkAssign = hasRole && (hasRole('admin') || hasRole('crew_admin'));

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="schedule-title">
                        Programación Comercial
                    </h1>
                    <p className="text-muted-foreground mt-1">Itinerarios mensuales de vuelos</p>
                </div>
                {canEdit && (
                    <div className="flex gap-2">
                        <Button onClick={() => setShowGenerate(true)} variant="outline" data-testid="generate-flights-btn">
                            <Calendar className="w-4 h-4 mr-2" /> Generar Vuelos
                        </Button>
                        <Button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-schedule-btn">
                            <Plus className="w-4 h-4 mr-2" /> Nueva Ruta
                        </Button>
                    </div>
                )}
            </div>

            {hasRole(['admin', 'crew_admin', 'ccod']) && <UnconfirmedCrewCard key={pendingKey} />}

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
                <Tabs value={viewMode} onValueChange={setViewMode} data-testid="schedule-view-tabs">
                    <TabsList className="mb-4">
                        <TabsTrigger value="list" data-testid="view-list-tab">
                            <Rows3 className="w-4 h-4 mr-2" />Lista
                        </TabsTrigger>
                        <TabsTrigger value="gantt" data-testid="view-gantt-tab">
                            <LayoutGrid className="w-4 h-4 mr-2" />Gantt / Calendario
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="list">
                <div className="grid gap-4" data-testid="schedules-grid">
                    {schedules.map((s) => (
                        <Card key={s.id} className="card-aviation" data-testid={`schedule-${s.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="flex items-center gap-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Plane className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-bold font-mono text-lg" data-testid={`schedule-fn-${s.id}`}>{s.flight_number}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {s.default_aircraft_type || 'Sin tipo asignado'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="font-mono font-bold">{s.origin}</span>
                                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                            <span className="font-mono font-bold">{s.destination}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                            <Clock className="w-3.5 h-3.5" />
                                            <span className="font-mono">{s.departure_time} - {s.arrival_time}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
                                            {[1,2,3,4,5,6,7].map(d => (
                                                <span key={d} className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${s.frequency?.includes(d) ? 'bg-primary/20 text-primary' : 'bg-muted/30 text-muted-foreground/40'}`}>
                                                    {dayLabels[d]}
                                                </span>
                                            ))}
                                        </div>
                                        <Badge variant="outline" className="text-xs font-mono">
                                            {s.effective_from || '?'} → {s.effective_to || '?'}
                                        </Badge>
                                        {s.paused && (
                                            <Badge className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/40" data-testid={`sched-paused-badge-${s.id}`}>
                                                <Pause className="w-3 h-3 mr-1" />PAUSADA
                                            </Badge>
                                        )}
                                        {canBulkAssign && (
                                            <Button variant="ghost" size="sm" onClick={() => openBulkDialog(s)}
                                                    data-testid={`sched-bulk-crew-${s.id}`} title="Asignar tripulación en bloque">
                                                <Users className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {canEdit && (
                                            <>
                                                <Button variant="ghost" size="sm" onClick={() => handleTogglePause(s)}
                                                        className={s.paused ? 'text-green-500 hover:text-green-600' : 'text-amber-500 hover:text-amber-600'}
                                                        data-testid={`sched-toggle-pause-${s.id}`}
                                                        title={s.paused ? 'Reanudar ruta' : 'Pausar ruta'}>
                                                    {s.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => openDuplicate(s)}
                                                        data-testid={`sched-duplicate-${s.id}`} title="Duplicar como plantilla">
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => openEditSchedule(s)} data-testid={`sched-edit-${s.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-600">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    {schedules.length === 0 && (
                        <Card className="card-aviation">
                            <CardContent className="p-12 text-center text-muted-foreground">
                                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No hay programaciones activas</p>
                                {canEdit && <p className="text-xs mt-1">Crea una nueva ruta para empezar</p>}
                            </CardContent>
                        </Card>
                    )}
                </div>
                    </TabsContent>
                    <TabsContent value="gantt">
                        <GanttView token={token} />
                    </TabsContent>
                </Tabs>
            )}

            {/* Create Schedule Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">{editingSchedule ? 'Editar Ruta' : 'Nueva Ruta'}</DialogTitle>
                        <DialogDescription>Define origen, destino, horarios y frecuencia. Marca &quot;Llega al dia siguiente&quot; si la ruta cruza medianoche.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>N. Vuelo</Label>
                                <Input value={form.flight_number} onChange={(e) => setForm({...form, flight_number: e.target.value.toUpperCase()})} placeholder="AD-401" className="input-aviation font-mono" data-testid="sched-flight-number" />
                            </div>
                            <div className="space-y-2">
                                <Label>Origen</Label>
                                <Input value={form.origin} onChange={(e) => setForm({...form, origin: e.target.value.toUpperCase()})} placeholder="BOG" maxLength={4} className="input-aviation font-mono" data-testid="sched-origin" />
                            </div>
                            <div className="space-y-2">
                                <Label>Destino</Label>
                                <Input value={form.destination} onChange={(e) => setForm({...form, destination: e.target.value.toUpperCase()})} placeholder="MDE" maxLength={4} className="input-aviation font-mono" data-testid="sched-destination" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Salida</Label>
                                <Input type="time" value={form.departure_time} onChange={(e) => setForm({...form, departure_time: e.target.value})} className="input-aviation font-mono" data-testid="sched-dep-time" />
                            </div>
                            <div className="space-y-2">
                                <Label>Llegada</Label>
                                <Input type="time" value={form.arrival_time} onChange={(e) => setForm({...form, arrival_time: e.target.value})} className="input-aviation font-mono" data-testid="sched-arr-time" />
                            </div>
                        </div>
                        {/* FASE 5: rutas que cruzan medianoche */}
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sched-arrives-next-day-label">
                            <input
                                type="checkbox"
                                checked={form.arrives_next_day}
                                onChange={(e) => setForm({ ...form, arrives_next_day: e.target.checked })}
                                className="w-4 h-4"
                                data-testid="sched-arrives-next-day"
                            />
                            <span className="text-muted-foreground">
                                Llega al día siguiente (ruta cruza medianoche)
                            </span>
                        </label>
                        <div className="space-y-2">
                            <Label>Tipo de Aeronave</Label>
                            <Select value={form.default_aircraft_type} onValueChange={(v) => setForm({...form, default_aircraft_type: v})}>
                                <SelectTrigger data-testid="sched-aircraft-type" className="input-aviation">
                                    <SelectValue placeholder="Seleccionar tipo de aeronave" />
                                </SelectTrigger>
                                <SelectContent>
                                    {[...new Set(aircraft.map(a => a.aircraft_type))].map(type => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Frecuencia</Label>
                            <div className="flex gap-1.5">
                                {[1,2,3,4,5,6,7].map(d => (
                                    <button key={d} type="button" onClick={() => toggleDay(d)}
                                        className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${form.frequency.includes(d) ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}
                                        data-testid={`sched-day-${d}`}
                                    >
                                        {dayLabels[d]}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Vigencia Desde</Label>
                                <Input type="date" value={form.effective_from} onChange={(e) => setForm({...form, effective_from: e.target.value})} className="input-aviation font-mono" data-testid="sched-from" />
                            </div>
                            <div className="space-y-2">
                                <Label>Vigencia Hasta</Label>
                                <Input type="date" value={form.effective_to} onChange={(e) => setForm({...form, effective_to: e.target.value})} className="input-aviation font-mono" data-testid="sched-to" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreate(false); setEditingSchedule(null); }}>Cancelar</Button>
                        <Button onClick={() => handleCreate()} disabled={submitting} className="btn-primary" data-testid="submit-schedule">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            {editingSchedule ? 'Guardar Cambios' : 'Crear Ruta'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <GenerateFlightsDialog open={showGenerate} onOpenChange={setShowGenerate} schedules={schedules} headers={headers} />

            {/* Auditoria 6 - Fase E Item 20: Duplicar Ruta Dialog */}
            <Dialog open={!!duplicateDialog} onOpenChange={(open) => { if (!open) setDuplicateDialog(null); }}>
                <DialogContent className="sm:max-w-[440px]" data-testid="duplicate-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">Duplicar Ruta</DialogTitle>
                        <DialogDescription>
                            Se creará una nueva ruta con los mismos horarios, frecuencia y rango de vigencia
                            que <span className="font-mono font-bold text-primary">{duplicateDialog?.flight_number}</span> pero con un flight_number distinto.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-3">
                        <div className="p-2 rounded bg-muted/30 text-xs text-muted-foreground">
                            Origen → destino: <span className="font-mono font-bold">{duplicateDialog?.origin}→{duplicateDialog?.destination}</span> ·
                            Horario: <span className="font-mono">{duplicateDialog?.departure_time}-{duplicateDialog?.arrival_time}</span>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Nuevo flight_number *</Label>
                            <Input value={duplicateFN} onChange={e => setDuplicateFN(e.target.value.toUpperCase())}
                                   placeholder="Ej: AN501" className="input-aviation font-mono" autoFocus
                                   data-testid="duplicate-flight-number" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDuplicateDialog(null)}>Cancelar</Button>
                        <Button onClick={handleDuplicate} className="btn-primary" data-testid="submit-duplicate">
                            <Copy className="w-4 h-4 mr-2" />Duplicar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Auditoria 6 - Fase E Item 21: Bulk Crew Assignment Dialog */}
            <Dialog open={!!bulkDialog} onOpenChange={(open) => { if (!open) setBulkDialog(null); }}>
                <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="bulk-assign-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">
                            Asignar Tripulación en Bloque · {bulkDialog?.flight_number}
                        </DialogTitle>
                        <DialogDescription>
                            Asigna la misma tripulación a todos los vuelos futuros de esta ruta dentro del rango seleccionado.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Desde</Label>
                                <Input type="date" value={bulkForm.date_from}
                                       onChange={e => setBulkForm({...bulkForm, date_from: e.target.value})}
                                       className="input-aviation font-mono" data-testid="bulk-date-from" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Hasta</Label>
                                <Input type="date" value={bulkForm.date_to}
                                       onChange={e => setBulkForm({...bulkForm, date_to: e.target.value})}
                                       className="input-aviation font-mono" data-testid="bulk-date-to" />
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={refreshBulkPreview} data-testid="bulk-refresh-preview">
                            <Calendar className="w-4 h-4 mr-2" />Actualizar preview
                        </Button>
                        {bulkPreview && (
                            <div className="p-2 rounded bg-muted/30 text-xs" data-testid="bulk-preview">
                                <p className="font-semibold text-primary">
                                    {bulkPreview.flights.length} vuelo(s) en el rango
                                </p>
                                <p className="text-muted-foreground">
                                    Con crew existente: {bulkPreview.flights.filter(f => f.has_crew).length} ·
                                    Sin crew: {bulkPreview.flights.filter(f => !f.has_crew).length}
                                </p>
                            </div>
                        )}

                        <div className="pt-2 border-t border-border/30 space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tripulación a asignar</p>
                                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={addBulkAssignment} data-testid="bulk-add-crew-btn">
                                    <Plus className="w-3 h-3 mr-1" />Añadir
                                </Button>
                            </div>
                            {bulkForm.assignments.length === 0 && (
                                <p className="text-[10px] text-muted-foreground italic">Añade al menos un tripulante para continuar.</p>
                            )}
                            {bulkForm.assignments.map((a, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_140px_36px] gap-2 items-center" data-testid={`bulk-crew-row-${idx}`}>
                                    <Select value={a.crew_id} onValueChange={v => updateBulkAssignment(idx, { crew_id: v })}>
                                        <SelectTrigger className="h-9 text-xs" data-testid={`bulk-crew-select-${idx}`}>
                                            <SelectValue placeholder="Selecciona tripulante..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {crewList.map(c => {
                                                const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;
                                                const alreadyPicked = bulkForm.assignments.some((x, i) => i !== idx && x.crew_id === c.id);
                                                return (
                                                    <SelectItem key={c.id} value={c.id} disabled={alreadyPicked}>
                                                        {name} · {FICHA_LABEL[c.position] || c.position}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    <Select value={a.role} onValueChange={v => updateBulkAssignment(idx, { role: v })}>
                                        <SelectTrigger className="h-9 text-xs" data-testid={`bulk-role-select-${idx}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="operating">Operando</SelectItem>
                                            <SelectItem value="deadhead_op">DH operacional</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button size="sm" variant="ghost" onClick={() => removeBulkAssignment(idx)} className="text-red-400 h-9 w-9 p-0"
                                            data-testid={`bulk-crew-remove-${idx}`}>
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <div className="pt-2 border-t border-border/30 space-y-1.5 text-xs">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={bulkForm.overwrite}
                                       onChange={e => setBulkForm({...bulkForm, overwrite: e.target.checked})}
                                       data-testid="bulk-overwrite" />
                                Sobrescribir vuelos que ya tienen tripulación asignada
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={bulkForm.force}
                                       onChange={e => setBulkForm({...bulkForm, force: e.target.checked})}
                                       data-testid="bulk-force" />
                                Forzar (ignorar conflictos de disponibilidad)
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkDialog(null)}>Cancelar</Button>
                        <Button onClick={submitBulkAssign} disabled={bulkSubmitting || !bulkPreview?.flights?.length} className="btn-primary" data-testid="submit-bulk-assign">
                            {bulkSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            <Users className="w-4 h-4 mr-2" />Asignar a {bulkPreview?.flights.length || 0} vuelo(s)
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
